import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export function StateFeesDialog({ open, onClose, mvrPrice }) {
  const [states, setStates] = useState([]);
  const [fallback, setFallback] = useState("");
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQ("");
    api.get("/state-fees").then(({ data }) => { setStates(data.states.map((s) => ({ ...s, fee: s.fee ?? "" }))); setFallback(data.default_state_fee ?? ""); }).catch((err) => toast.error(formatApiError(err)));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/state-fees", { fees: Object.fromEntries(states.map((s) => [s.state, s.fee === "" ? null : Number(s.fee)])), default_state_fee: fallback === "" ? null : Number(fallback) });
      toast.success("State MVR fees saved — new requests use the updated fees");
      onClose(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const shown = states.filter((s) => !q || s.name.toLowerCase().includes(q.toLowerCase()) || s.state.toLowerCase() === q.toLowerCase());
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="state-fees-dialog">
        <DialogHeader><DialogTitle>State MVR fees</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">What each DMV charges for a driving record. The customer sees one combined MVR price: <b>your service fee {mvrPrice != null ? `($${Number(mvrPrice).toFixed(2)})` : ""} + the driver's license-state fee</b>. Your service fee is the <b>MVR row's Price</b> in Report pricing — set it to your markup only (e.g. $9.00), not the total. Enter each state's DMV fee below exactly as your vendor bills it; the "=" column shows what the customer pays.</p>
        <div className="flex items-end gap-3">
          <div className="flex-1"><Label>Find state</Label><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Texas or TX" data-testid="state-fees-search" /></div>
          <div className="w-44"><Label>Fallback fee (blank states)</Label><Input type="number" step="0.01" min="0" value={fallback} onChange={(e) => setFallback(e.target.value)} className="font-mono" data-testid="state-fees-default-input" /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-6 border border-slate-200 rounded-md p-2 max-h-[50vh] overflow-y-auto">
          {shown.map((s) => (
            <div key={s.state} className="flex items-center justify-between gap-2 py-1 border-b border-slate-100 text-sm">
              <span><span className="font-mono text-xs text-slate-500 w-7 inline-block">{s.state}</span>{s.name}</span>
              <div className="flex items-center gap-2">
                <Input type="number" step="0.01" min="0" value={s.fee} placeholder="—" className="h-7 w-24 font-mono text-right" onChange={(e) => setStates(states.map((x) => (x.state === s.state ? { ...x, fee: e.target.value } : x)))} data-testid={`state-fee-input-${s.state}`} />
                {mvrPrice != null && s.fee !== "" && <span className="font-mono text-xs text-slate-500 w-16 text-right" title="Combined MVR price">= ${(Number(mvrPrice) + Number(s.fee)).toFixed(2)}</span>}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="state-fees-save-button">{saving ? "Saving…" : "Save state fees"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
